import { useQuery } from "@apollo/client";
import {
  chainDynamicValue,
  DynamicValue,
  useComponentState,
} from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { RefObject, useEffect, useState } from "react";
import {
  Accordion,
  AccordionCollapse,
  AccordionToggle,
  Button,
  Form,
} from "react-bootstrap";
import {
  defaultFilter,
  FiltersState,
  recommendedBodyParts,
  recommendedEquipments,
  recommendedFocusTypes,
  recommendedSkills,
  recommendedStyleTypes,
} from "src/components/Filter";
import FiltersSet from "src/components/FilterSet";
import Heading from "src/components/Heading";
import Translate from "src/components/Translate";
import constants from "src/constants";
import Chevron_left from "src/icons/Chevron-left";
import { profileNames } from "src/queries/profileNames";
import { sButton } from "src/styles/sButton.module.scss";
import { sFilterAccordion } from "src/styles/sFilterAccordion.module.scss";
import { sFiltersBlock } from "src/styles/sFiltersBlock.module.scss";
import { filtersModal } from "src/styles/sFiltersModal.module.scss";
import { sSwitch } from "src/styles/sSwitch.module.scss";

type Props = {
  filtersState: DynamicValue<FiltersState>;
  openFilters: DynamicValue<boolean>;
  numberOfItems: number;
  filtersModalRef: RefObject<any>;
};

export default function FiltersModal({
  filtersState,
  openFilters,
  numberOfItems,
  filtersModalRef,
}: Props) {
  const $paid = chainDynamicValue(
    filtersState,
    "paid"
  ) as DynamicValue<boolean>;
  const $subscription = chainDynamicValue(
    filtersState,
    "subscription"
  ) as DynamicValue<boolean>;
  // const $favourite = chainDynamicValue(
  //   filtersState,
  //   "favourite"
  // ) as DynamicValue<boolean>;
  const $rating = chainDynamicValue(
    filtersState,
    "rating"
  ) as DynamicValue<number>;

  const data = () => {};

  const [bodyParts, setBodyParts] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [durations, setDurations] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [focusTypes, setFocusTypes] = useState([]);
  const [skills, setSkills] = useState([]);
  const [styleTypes, setStyleTypes] = useState([]);
  const [languages, setLanguages] = useState([]);

  async function getUsedFilters() {
    const data = fetch(`${constants.Paths.apiUrl}/products/used-filters`).then(
      (res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error("API Error");
        }
      }
    );

    const loadedData = await Promise.all([data]);
    setBodyParts(loadedData[0].body_parts);
    setDifficulties(loadedData[0].difficulty);
    setDurations(loadedData[0].duration);
    setEquipments(loadedData[0].equipment);
    setFocusTypes(loadedData[0].focuses);
    setSkills(loadedData[0].skills);
    setStyleTypes(loadedData[0].styles);
    setLanguages(loadedData[0].languages);
  }

  useEffect(() => {
    getUsedFilters();
  }, []);

  const $profilesSearch = useComponentState("");
  const profilesData = useQuery(profileNames, {
    errorPolicy: "all",
  });

  function cleanName(name: string) {
    return name.replace(/[ ]+/g, " ").trim();
  }
  const allProfiles =
    profilesData.data?.profiles?.map((profile) => {
      const generatedName = cleanName(
        (profile?.client?.name || "") + " " + (profile?.client?.surname || "")
      );
      const nickname = cleanName(profile.nickname);
      const addition = generatedName !== nickname ? " (" + nickname + ")" : "";
      return { title: generatedName + addition, value: profile.slug };
    }) || [];

  const filteredProfiles = $profilesSearch.value
    ? allProfiles.filter(({ title }) =>
        title.toLowerCase().includes($profilesSearch.value.toLowerCase())
      )
    : allProfiles;

  return (
    <div
      className={composeClassNames([filtersModal, "filter-modal"])}
      ref={filtersModalRef}
    >
      <div className={composeClassNames(["filter-header"])}>
        <Heading
          type="h5"
          title={<Translate tKey="Filter" />}
          className={composeClassNames(["filter-headline"])}
        />
        <div className={`count`}>
          &nbsp;({numberOfItems} <Translate tKey={`items`} />)
        </div>
        <Button
          variant="link"
          className={composeClassNames([sButton, "filter-clear"])}
          onClick={() => filtersState.change(defaultFilter)}
        >
          <Translate tKey={`clear filters`} />
        </Button>
        <Chevron_left
          className={composeClassNames(["filter-close"])}
          onClick={() => openFilters.change(false)}
        />
      </div>
      <div className={composeClassNames([sFilterAccordion])}>
        <div className={composeClassNames(["accordion-header"])}>
          <Heading type="h5" title={<Translate tKey="Basic" />} />
        </div>
        {/* </AccordionToggle>
        <AccordionCollapse eventKey={`0`}> */}
        <div className={composeClassNames([sFiltersBlock])}>
          <Form.Check
            id="custom-switch"
            type="switch"
            label={<Translate tKey="paid" />}
            className={composeClassNames([sSwitch])}
            checked={$paid.value ? true : false}
            onChange={(e) => {
              $paid.change(e.target.checked);
            }}
          />
          <Form.Check
            id="custom-switch1"
            type="switch"
            label={<Translate tKey="Subscription" />}
            className={composeClassNames([sSwitch])}
            checked={$subscription.value ? true : false}
            onChange={(e) => {
              $subscription.change(e.target.checked);
            }}
          />
          {/* Favourite is not working on backend yet */}
          {/* <Form.Check
          type="checkbox"
          checked={$favourite.value}
          onChange={(e) => {
            $favourite.change(e.target.checked);
          }}
          label="Favourite"
        /> */}
          {constants.Filter.ShowRating && (
            <>
              <Form.Label htmlFor="rating">
                <Translate tKey="Rating (no less than)" />
              </Form.Label>
              <Form.Control
                type="number"
                value={$rating.value}
                onChange={(e) => {
                  $rating.change(Number(e.target.value));
                }}
                id="rating"
              />
            </>
          )}
        </div>
      </div>

      <Accordion
        className={composeClassNames([sFilterAccordion])}
        defaultActiveKey="0"
      >
        <AccordionToggle
          eventKey={`0`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Language" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`0`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "language") as any}
            all={languages}
          />
        </AccordionCollapse>
      </Accordion>

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`1`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Difficulty" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`1`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "difficulty") as any}
            all={difficulties}
          />
        </AccordionCollapse>
      </Accordion>

      {constants.Filter.ShowDuration && (
        <Accordion className={composeClassNames([sFilterAccordion])}>
          <AccordionToggle
            eventKey={`6`}
            className={composeClassNames([
              sFilterAccordion,
              "accordion-header",
            ])}
          >
            <Heading type="h5" title={<Translate tKey="Duration" />} />
          </AccordionToggle>
          <AccordionCollapse eventKey={`6`}>
            <FiltersSet
              set={chainDynamicValue(filtersState, "duration") as any}
              all={durations}
            />
          </AccordionCollapse>
        </Accordion>
      )}

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`2`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Focus" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`2`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "focusType") as any}
            all={focusTypes}
            recommended={recommendedFocusTypes}
          />
        </AccordionCollapse>
      </Accordion>

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`4`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Style" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`4`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "styleType") as any}
            all={styleTypes}
            recommended={recommendedStyleTypes}
          />
        </AccordionCollapse>
      </Accordion>

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`3`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Skill" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`3`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "skills") as any}
            all={skills}
            recommended={recommendedSkills}
          />
        </AccordionCollapse>
      </Accordion>

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`5`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Body part" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`5`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "bodyPart") as any}
            all={bodyParts}
            recommended={recommendedBodyParts}
          />
        </AccordionCollapse>
      </Accordion>

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`5`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Equipment" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`5`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "equipment") as any}
            all={equipments}
            recommended={recommendedEquipments}
          />
        </AccordionCollapse>
      </Accordion>

      <Accordion className={composeClassNames([sFilterAccordion])}>
        <AccordionToggle
          eventKey={`5`}
          className={composeClassNames([sFilterAccordion, "accordion-header"])}
        >
          <Heading type="h5" title={<Translate tKey="Profile" />} />
        </AccordionToggle>
        <AccordionCollapse eventKey={`5`}>
          <FiltersSet
            set={chainDynamicValue(filtersState, "instructor") as any}
            all={filteredProfiles}
          />
        </AccordionCollapse>
      </Accordion>
    </div>
  );
}
